import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { PLAN_NAMES, defaultRange, money, num, rangeParams, usd, useLoad, when } from "@/lib/staff";
import { Badge, Card, ErrorBox, RangePicker, Skeleton, Stat, Table } from "@/components/staff/ui";
import { RevenueSummary } from "@/components/staff/RevenueSummary";

const PAY_COLS = [
  { key: "created", label: "Date", render: (r) => <span className="whitespace-nowrap text-xs">{when(r.created)}</span> },
  { key: "email", label: "Student", render: (r) => (r.user_id ? <Link to={`/staff/students/${r.user_id}`} className="text-xs text-sky-300 hover:underline">{r.email}</Link> : <span className="text-xs text-neutral-500">Not linked</span>) },
  { key: "description", label: "Description", render: (r) => <span className="text-xs text-neutral-400 line-clamp-1">{r.description || "—"}</span> },
  { key: "amount", label: "Amount", right: true, render: (r) => money({ [r.currency]: r.amount }) },
  { key: "refunded", label: "Refunded", right: true, render: (r) => (r.refunded ? money({ [r.currency]: r.refunded }) : "—") },
  { key: "link", label: "", right: true, render: (r) => <a href={r.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-300 hover:underline" data-testid={`billing-payment-link-${r.id}`}>Stripe<ExternalLink className="h-3 w-3" /></a> },
];

const SUB_COLS = [
  { key: "tier", label: "Plan", render: (r) => PLAN_NAMES[r.tier] || r.tier },
  { key: "status", label: "Status", render: (r) => <Badge tone={r.status === "active" ? "green" : r.status === "past_due" ? "amber" : "neutral"}>{r.status}</Badge> },
  { key: "renewal", label: "Renewal", render: (r) => (r.renewal === "ending" ? "Ends at period end" : "Renews") },
  { key: "count", label: "Students", right: true, render: (r) => num(r.count) },
];

function Billed({ b }) {
  if (!b.available) return <Card title="Billed by Stripe" testid="billing-stripe-card"><p className="text-sm text-amber-200" data-testid="billing-stripe-unavailable">{b.message}</p></Card>;
  return (
    <Card title="Billed by Stripe" testid="billing-stripe-card" action={<Badge tone={b.mode === "live" ? "green" : "amber"} testid="billing-stripe-mode">{b.mode === "live" ? "Live" : "Test"} mode</Badge>}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
        <p><span className="block text-xs text-neutral-500">Charged</span><span className="tabular-nums" data-testid="billing-gross">{money(b.gross)}</span></p>
        <p><span className="block text-xs text-neutral-500">Refunded</span><span className="tabular-nums" data-testid="billing-refunded">{money(b.refunded)}</span></p>
        <p><span className="block text-xs text-neutral-500">Net</span><span className="tabular-nums" data-testid="billing-net">{money(b.net)}</span></p>
        <p><span className="block text-xs text-neutral-500">Payments</span><span className="tabular-nums" data-testid="billing-payments">{num(b.payments)}</span></p>
      </div>
    </Card>
  );
}

export function BillingTab() {
  const [range, setRange] = useState(() => defaultRange("30d"));
  const params = useMemo(() => rangeParams(range), [range]);
  const { data: d, error, loading, reload } = useLoad("/staff/billing", params);
  return (
    <div className="space-y-4" data-testid="billing-tab">
      <RangePicker value={range} onChange={setRange} testid="billing-range" />
      {error && <ErrorBox error={error} onRetry={reload} />}
      {!d && loading && <Skeleton rows={5} />}
      {d && (
        <>
          <Billed b={d.billed} />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Stat label="Estimated AI cost (same dates)" value={usd(d.est_ai_cost)} hint="Our estimate, kept separate from Stripe amounts" testid="billing-est-ai-cost" />
            <Stat label="Free plans given by staff (active)" value={num(d.free_plans)} testid="billing-free-plans" />
            <Stat label="Free passes given by staff (unused or running)" value={num(d.free_passes)} testid="billing-free-passes" />
          </div>
          <Table testid="billing-subscriptions" columns={SUB_COLS} rows={d.subscriptions} rowKey={(r) => `${r.tier}-${r.status}-${r.renewal}`} empty="No subscriptions yet." maxH="max-h-80" />
          {d.billed.available && <Table testid="billing-recent-payments" columns={PAY_COLS} rows={d.billed.recent} rowKey={(r) => r.id} empty="No payments in this range." maxH="max-h-96" />}
        </>
      )}
      <div className="pt-4"><RevenueSummary /></div>
    </div>
  );
}
